/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Redirect} from "react-router";
// material
import {Page, Content, InContentMessage, Icon, Button, history, displayModal, ToolTip} from '@insticator/insticator-ui'; // iui-material
import Header from '../../../../material/structure/header/header';
import PageHeader from '../../../../material/structure/pageheader/pageheader';
import CardWrapper from "../../../../material/cardwrapper/cardwrapper";
import SectionGroup from "../../../../material/expandablesection/sectiongroup/sectiongroup";
import SectionLoader from "../../../../material/sectionloader/sectionloader";
import ExpandableListTable from "../../../../material/expandablelisttable/expandablelisttable";
// utils

// thunks
import {deleteReport, downloadReport, fetchAllReportsByAccount} from "../../../../state/thunks/report";
import {refresh, resetDownloadStatus, toggleIsOpened} from "../../../../state/slices/report";

// styles
import injectSheet from 'react-jss';
import styles from './allreports_styles';

// components
import MonetizationForm from '../monetizationform/monetizationformv2';
import DisabledGlobalFilter from "../monetizationform/filter/disabledglobalfilter/disabledglobalfilter";

// static
import {
    DATE, END_DATE,
    FORM_HEADERS,
    FREQUENCY, FREQUENCY_MESSAGE_DAILY,
    FREQUENCY_MESSAGE_MONTHLY, FREQUENCY_MESSAGE_WEEKLY,
    NOT_AVAILABLE,
    REPORT_CREATED,
    REPORT_TYPE, START_DATE
} from "../monetizationform/consts";
import ApprovePopup from "../../commenting/commentingconfig/requestcommentwidget/approvepopup/approvepopup";
import ApproveModal from "./approvemodal";
import Tableheader from "../../../../material/structure/tableheader/tableheader";
import {AVG_RPM, PAGE_VIEWS, PRODUCT_TYPE} from "../../monetization/consts";

//utils
import {getDayOfTheWeek} from "../../../../utils/date";


/* ========== ~~~~~~~~~~ REPORT BUILDER / ALL REPORTS ~~~~~~~~~~ ========== */
export default injectSheet(styles)(({classes}) => {
    // states
    const accountUUID = useSelector((state) => state.session.accountUUID);
    const mocked = useSelector((state) => state.session.mocked);
    const allReports= mocked ? useSelector((state) => state.report.allReports) : useSelector((state) => state.report.allReports).filter(report => !report.name.toLowerCase().startsWith("int_"));
    const reportRequestStatus = useSelector((state) => state.report.reportRequestStatus);
    const allReportsReceived = useSelector((state) => state.report.allReportsReceived);
    const reportDeleted = useSelector((state) => state.report.reportDeleted);
    const reportUpdated = useSelector((state) => state.report.reportUpdated);
    const downloadStatus = useSelector((state) => state.report.downloadStatus);
    const error = useSelector((state) => state.report.error);

    const [hoveredRow, setHoveredRow] = useState();

    // actions
    const dispatch = useDispatch();

    useEffect(() => {
        if (accountUUID) dispatch(fetchAllReportsByAccount(accountUUID));
    }, [accountUUID])

    useEffect(() => {
        if (reportUpdated || reportDeleted) {
            dispatch(refresh())
            dispatch(fetchAllReportsByAccount(accountUUID));
        }
    }, [reportUpdated, reportDeleted])

    useEffect(() => {
        if (Object.keys(downloadStatus).length) Object.keys(downloadStatus).forEach((uuid) => {
            const status = downloadStatus[uuid];
            if (status === 'success' || status === 'error' || status === 'processing') setTimeout(() => {
                    dispatch(resetDownloadStatus(uuid))
                }, 10000);
        })
    }, [downloadStatus])

    const cancel = () => dispatch(displayModal(false));
    const handleEdit = (uuid) => dispatch(toggleIsOpened(uuid));
    const handleDelete = (uuid) => {
        return dispatch(displayModal(true,
            <ApproveModal handleCancel={cancel} handleApprove={() => {
                dispatch(deleteReport(uuid));
                dispatch(displayModal(false));
            }} />
        ));
    };
    const handleDownload = (uuid) => {
        dispatch(downloadReport(uuid))
    };

    const getIcon = (uuid) => {
        const status = downloadStatus[uuid];
        if (status === 'success') return 'check';
        if (status === 'pending') return 'arrow-down';
        if (status === 'processing') return 'clock';
        if (status === 'error') return 'exclamation';
        return 'download';
    }

    const getReportFrequency = (frequency, startDate) => {
        switch(frequency) {
            case 'Monthly':
                return `${getOrdinal(new Date(startDate).getDate())} of Every Month`;
            case 'Weekly':
                return `Weekly on ${getDayOfTheWeek(startDate)}`;
            case 'Daily':
                return `Daily`
            default:
                return '-'
        }
    }

    const getOrdinal = (num) => {
        let suffix = 'th';
        if (num % 10 === 1 && num % 100 !== 11) {
            suffix = 'st';
        } else if (num % 10 === 2 && num % 100 !== 12) {
            suffix = 'nd';
        } else if (num % 10 === 3 && num % 100 !== 13) {
            suffix = 'rd';
        }
        return num + suffix;
    }

    const tableData = allReports.map(report => {
        const {frequency, startDate, endDate, recipientEmails} = report.schedule;
        const isDelieveryEnabled = frequency && startDate && endDate && recipientEmails.length > 0;
        const frequencyText = ((frequency || '').charAt(0)) + (frequency || '').toLowerCase().substr(1);
        const createdAtDateFormat = {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        };
        const creationDate = new Date(report.createdAt).toLocaleString('en-US', createdAtDateFormat);
        return ({
                name: report.name,
                frequency: isDelieveryEnabled ? getReportFrequency(frequencyText, startDate) : '-',
                endDate: isDelieveryEnabled
                  ? new Date(endDate).toLocaleString('en-us', { month : 'short', day : 'numeric', year : 'numeric'})
                  : '-',
                recipients: (report.schedule?.recipientEmails || []).length || '-',
                creationDate: creationDate,
                expandable: <MonetizationForm uuid={report.reportUUID} reportActionType={'Edit Report'} hoveredRow={hoveredRow}/>,
                actions: <div className={classes.actions} onMouseEnter={() => setHoveredRow(report.reportUUID)} onMouseLeave={() => setHoveredRow(null)}>
                    <Icon name={`edit`} handleClick={() => handleEdit(report.reportUUID)}/>
                    {downloadStatus[report.reportUUID] === 'processing'
                        ? <ToolTip position="top" action="mouseenter" content="Report is being generated, please try again shortly">
                            <Icon name={getIcon(report.reportUUID)} handleClick={() => handleDownload(report.reportUUID)}/>
                          </ToolTip>
                        : <Icon name={getIcon(report.reportUUID)} handleClick={() => handleDownload(report.reportUUID)}/>
                    }
                    <Icon name={`trash`} handleClick={() => handleDelete(report.reportUUID)}/>
                </div>
            })
        }
    )


    if (allReportsReceived && !allReports.length) return <Redirect to={'/dashboard/reportbuilder/new'}/>

    if (reportRequestStatus === 'error') {
        if (error.includes('404')) return <Redirect to={'/error/404'}/>
        return <Redirect to={'/error/500'}/>
    }

    if (reportRequestStatus === 'pending' || !allReportsReceived) return <SectionLoader/>

    return (
        <Page name={`Report Builder`} klass={classes.reportBuilder}>
            <Content>
                <InContentMessage />
                <PageHeader title={'Report Builder'}>
                </PageHeader>
                <CardWrapper klass={classes.wrapper}>
                    <SectionGroup>
                        <Tableheader title={'Reports'} actionButton={
                            <Button
                                variant={'primary'}
                                text={'Create New Report'}
                                type={'action'}
                                handleClick={() => history.push('/dashboard/reportbuilder/new?skipbutton=true')}
                            />} ></Tableheader>
                        <ExpandableListTable
                            columnHeads={FORM_HEADERS}
                            data={tableData}
                            tableClass={classes.allReportsTable}
                            tableBodyClass={classes.allReportsTableBody}
                            tableHeaderClass={classes.allReportsTableHeader}
                        />
                    </SectionGroup>
                </CardWrapper>
            </Content>
        </Page>
    )
});
